import { get, post, globalPut, remove, getBlob } from '../index';

// Invoice Management API helpers. Mirrors the other feature api/ modules
// (thin wrappers over the shared get/post/put/remove that prepend the base
// URL + JWT). Backend routes are mounted at /api/invoices.

// Invoices.
export const listInvoices = (brandId, params = {}) => {
  const q = new URLSearchParams({
    ...(brandId ? { brand_id: brandId } : {}),
    ...params,
  }).toString();
  return get(`/api/invoices${q ? `?${q}` : ''}`);
};
export const getInvoice = (id) => get(`/api/invoices/${id}`);
export const createInvoice = (body) => post('/api/invoices', body, false);
export const updateInvoice = (id, body) => globalPut(`/api/invoices/${id}`, body, false);
export const deleteInvoice = (id) => remove(`/api/invoices/${id}`);
// Compile the invoice into its branded PDF/HTML artifact.
export const generateInvoice = (id) => post(`/api/invoices/${id}/generate`, {}, false);
// The other way in: the operator already has the finished PDF. Same lists, same
// per-brand payout, same reminder flows — only the document differs.
export const uploadInvoice = (body) => post('/api/invoices/upload', body, false);
// The paid checkbox. Ticking it completes every flow chasing this invoice and
// blocks all future email about it.
export const setInvoicePaid = (id, paid, paidAmount) =>
  globalPut(`/api/invoices/${id}/paid`, { paid, ...(paidAmount != null ? { paidAmount } : {}) }, false);

// THE DOCUMENT, OPENABLE.
//
// The invoice itself — the uploaded PDF for an uploaded one, the compiled
// artifact for a built one, compiled on the spot if it never has been. Bytes,
// not JSON: the browser renders a PDF natively and a base64 string is neither
// cheap to ship nor viewable.
export const fetchInvoiceDocument = (id) => getBlob(`/api/invoices/${id}/document`);
// What the NEXT reminder will attach, resolved through the same order of
// preference the worker's drafter uses — so what is reviewed is what is sent.
export const fetchReminderAttachment = (id) => getBlob(`/api/invoices/reminders/${id}/attachment`);
// An attachment on a reminder already sent. Addressed through the flow, so
// invoice permissions cannot reach mail outside this series.
export const fetchSentReminderAttachment = (reminderId, messageId, idx) =>
  getBlob(`/api/invoices/reminders/${reminderId}/messages/${messageId}/attachment/${idx}`);

// The mailboxes a reminder can send FROM. Gated by INVOICE permissions, unlike
// /api/email/accounts which needs the email package — a user with invoicing but
// not email got a 403 there and an empty picker with nothing saying why.
export const listReminderMailboxes = () => get('/api/invoices/mailboxes');

// The Stripe page for exactly this invoice. Created on request so it can be
// checked BEFORE a client sees it — the reminder series only includes it from
// the third email either way.
// Off the board, still on the record — and it takes the invoice's reminder loop
// with it, so an archived invoice cannot still be chasing somebody.
export const archiveInvoice = (id, archived = true) =>
  post(`/api/invoices/${id}/archive`, { archived }, false);

// Creates it NOW and answers with the URL — or with the reason there is none
// (422: no Stripe key for the brand, a zero total, a currency Stripe will not
// take). It used to reply "queued" and leave the form polling in the dark,
// which is how "Create link does nothing" happened.
export const createPaymentLink = (id) => post(`/api/invoices/${id}/payment-link`, {}, false);
// Whether a link CAN be made, asked when an invoice is picked so the blocker is
// on screen next to the button rather than discovered by pressing it.
export const getPaymentLinkStatus = (id) => get(`/api/invoices/${id}/payment-link`);

// The brand's own Stripe account. The key is write-only: this reports whether
// one is configured and which account it is, never the key itself.
export const getBrandStripeAccount = (brandId) => get(`/api/invoices/stripe-account?brand_id=${encodeURIComponent(brandId)}`);
export const setBrandStripeAccount = (brandId, secretKey, label = '') =>
  globalPut('/api/invoices/stripe-account', { brand_id: brandId, secretKey, label }, false);

// The brand's postal address — the "From" block on every invoice it sends.
export const getBrandAddress = (brandId) => get(`/api/invoices/brand-address?brand_id=${encodeURIComponent(brandId)}`);
export const setBrandAddress = (brandId, mailingAddress) =>
  globalPut('/api/invoices/brand-address', { brand_id: brandId, mailingAddress }, false);

// Reminder flows.
export const listReminders = (params = {}) => {
  const q = new URLSearchParams(params).toString();
  return get(`/api/invoices/reminders${q ? `?${q}` : ''}`);
};
export const createReminder = (body) => post('/api/invoices/reminders', body, false);
export const updateReminder = (id, body) => globalPut(`/api/invoices/reminders/${id}`, body, false);
export const deleteReminder = (id) => remove(`/api/invoices/reminders/${id}`);
// Put a flow away without destroying it. Delete loses the record of what was
// chased and when, which is the history of the debt; archive keeps it and stops
// it sending. Pass archived:false to bring it back exactly as it was.
export const archiveReminder = (id, archived = true, reason = '') =>
  post(`/api/invoices/reminders/${id}/archive`, { archived, reason }, false);
export const confirmPayment = (id, body = {}) => post(`/api/invoices/reminders/${id}/confirm-payment`, body, false);
// The whole series: what was sent, and what goes next with its date.
export const getReminderTimeline = (id) => get(`/api/invoices/reminders/${id}/timeline`);
// Write the next reminder EARLY so it can be read and changed before the
// client sees it. The worker's own drafter does the writing.
export const predraftReminder = (id) => post(`/api/invoices/reminders/${id}/predraft`, {}, false);
// Edit that parked draft — subject, body, recipients, CCs, or the send date.
export const editReminderDraft = (id, body) => globalPut(`/api/invoices/reminders/${id}/draft`, body, false);

// THE SEND GATE. A flow sends nothing until this is switched on, and switching
// it on makes it due immediately — turning it on IS the send. The ONE exception
// is a flow carrying a `scheduledStartAt` still in the future: that arms it for
// the chosen date instead, and nothing is emailed now.
//
// Resolves to the WHOLE response rather than just `data`, because the half that
// distinguishes those two outcomes is `armedFor` — "sending is on" reads
// identically either way and means opposite things on the day.
export const setReminderSending = (id, enabled) =>
  globalPut(`/api/invoices/reminders/${id}/sending`, { enabled }, false);
export const recordReply = (id, replyText) => post(`/api/invoices/reminders/${id}/record-reply`, { replyText }, false);

// Analytics.
export const getInvoiceAnalytics = (brandId) =>
  get(`/api/invoices/analytics${brandId ? `?brand_id=${encodeURIComponent(brandId)}` : ''}`);

// Clients + their projects. The list comes back with each client's projects
// attached, because the tab shows both and two round trips would only make the
// page render half of itself first.
export const listClients = (brandId) =>
  get(`/api/clients${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`);
export const createClient = (body) => post('/api/clients', body, false);
export const updateClient = (id, body) => globalPut(`/api/clients/${id}`, body, false);
export const deleteClient = (id) => remove(`/api/clients/${id}`);
export const createClientProject = (clientId, body) => post(`/api/clients/${clientId}/projects`, body, false);
export const updateClientProject = (projectId, body) => globalPut(`/api/clients/projects/${projectId}`, body, false);
export const deleteClientProject = (projectId) => remove(`/api/clients/projects/${projectId}`);

// BANK ACCOUNTS — the per-brand catalogue a brand bills FROM.
//
// A named list rather than the single standing account on BrandSettings, so a
// brand with a USD ACH account and a EUR IBAN picks between them on the invoice
// instead of retyping a routing number from memory. Gated on the INVOICE
// permissions: these carry account numbers, and anyone who can build an invoice
// already decides what bank details go on it.
export const listBankAccounts = (brandId) =>
  get(`/api/bank-accounts${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`);
export const createBankAccount = (body) => post('/api/bank-accounts', body, false);
export const updateBankAccount = (id, body) => globalPut(`/api/bank-accounts/${id}`, body, false);
export const deleteBankAccount = (id) => remove(`/api/bank-accounts/${id}`);

// Weekly timesheet. `week` is any ISO date inside the week you want; the
// server resolves it to that Monday, so the client never has to agree with it
// about where a week starts.
export const listTimeEntries = (params = {}) => {
  const q = new URLSearchParams(params).toString();
  return get(`/api/time-entries${q ? `?${q}` : ''}`);
};
export const createTimeEntry = (body) => post('/api/time-entries', body, false);
export const updateTimeEntry = (id, body) => globalPut(`/api/time-entries/${id}`, body, false);
export const deleteTimeEntryWeek = (id, week) =>
  remove(`/api/time-entries/${id}?week=${encodeURIComponent(week)}`);
export const deleteTimeEntry = (id) => remove(`/api/time-entries/${id}?all=1`);
export const copyPreviousWeek = (week) => post('/api/time-entries/copy-previous', { week }, false);
export const getTimeRollup = (params = {}) => {
  const q = new URLSearchParams(params).toString();
  return get(`/api/time-entries/rollup${q ? `?${q}` : ''}`);
};
